import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Star, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { API, LEVELS, StarScore, fmtDate, level, useStarColors } from '../components/five-star/stars';
import { GoldBadge, Repeats, Trend } from './FirmEvaluationPage';

const openPdf = async (url, name, download = false) => {
  try {
    const r = await axios.get(`${API}/firm/five-star/${url}`, { responseType: 'blob' });
    const href = URL.createObjectURL(r.data);
    if (!download) return window.open(href, '_blank');
    const a = document.createElement('a'); a.href = href; a.download = name; a.click();
  } catch { toast.error('PDF açılamadı'); }
};

const FirmStar = ({ f }) => {
  const c = useStarColors();
  const col = f.avg ? c[level(f.avg)] : '#94a3b8';
  return (
    <div className="flex items-center gap-5 rounded-xl border bg-slate-950 p-5 text-white" data-testid={`firm-star-${f.id}`}>
      <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-xl border-2" style={{ borderColor: col }}>
        <Star className="h-12 w-12 fill-current" style={{ color: col }} />
        <b className="-mt-1 text-lg" style={{ color: col }}>{f.avg?.toFixed(2) ?? '-'}</b>
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center text-lg font-bold">{f.name}{f.gold && <GoldBadge id={f.id} />}{f.is_sub && <span className="ml-2 rounded bg-sky-600 px-2 py-0.5 text-[11px] font-semibold">Alt firma</span>}</p>
        <p className="text-sm" style={{ color: col }}>{f.avg ? `${LEVELS[level(f.avg)]} · ${f.avg} / 5` : 'Denetim yok'}</p>
        <p className="mt-1 text-xs text-slate-400">{f.jobs} iş · {f.audits} denetim{f.low_data ? ' · Az veri (3 işten az)' : ''}</p>
      </div>
      <Button size="sm" variant="secondary" onClick={() => openPdf(`companies/${f.id}/pdf`, `five_star_karne_${f.name}.pdf`, true)} data-testid={`firm-card-pdf-${f.id}`}><Download className="mr-1 h-4 w-4" />Karne PDF</Button>
    </div>
  );
};

const AuditRow = ({ a }) => (
  <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`firm-audit-${a.id}`}>
    <div className="min-w-0 flex-1">
      <p>{a.is_new && <span className="mr-2 rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`firm-audit-new-${a.id}`}>Yeni</span>}<b className="font-mono text-xs">{a.request_number || '-'}</b> · {fmtDate(a.created_at)} · {a.location_name} · {a.is_tipi} / {a.is_cesidi}
        {a.is_sub && <span className="ml-2 rounded bg-sky-100 px-1.5 text-[11px] font-semibold text-sky-700">{a.company_name} adına</span>}</p>
      {a.isg_note && <p className="mt-0.5 text-xs text-slate-500">İSG açıklaması: {a.isg_note}</p>}
    </div>
    <StarScore value={a.score} />
    <Button size="sm" variant="outline" onClick={() => openPdf(`audits/${a.id}/pdf`)} data-testid={`firm-audit-view-${a.id}`}><FileText className="mr-1 h-4 w-4" />Görüntüle</Button>
    <Button size="sm" variant="ghost" onClick={() => openPdf(`audits/${a.id}/pdf`, `five_star_${a.request_number || a.id}.pdf`, true)} data-testid={`firm-audit-download-${a.id}`}><Download className="h-4 w-4" /></Button>
  </div>
);

const EmployeeList = ({ rows }) => (
  <Accordion type="single" collapsible className="rounded-lg border bg-white">
    {rows.map(e => (
      <AccordionItem key={e.id} value={e.id} className="px-4" data-testid={`firm-emp-${e.id}`}>
        <AccordionTrigger className="hover:no-underline">
          <div className="flex flex-1 items-center gap-3 text-left">
            <div className="min-w-0 flex-1"><p className="flex items-center font-semibold text-slate-900">{e.name}{e.gold && <GoldBadge id={e.id} label="Altın Personel" />}</p><p className="text-xs text-slate-500">{e.position || '-'} · {e.company_name}</p></div>
            <span className="mr-3"><StarScore value={e.avg} jobs={e.jobs} lowData={e.low_data} /></span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="mb-2 flex justify-end"><Button size="sm" variant="outline" onClick={() => openPdf(`employees/${e.id}/pdf`, `five_star_personel_${e.name}.pdf`, true)} data-testid={`firm-emp-pdf-${e.id}`}><Download className="mr-1 h-4 w-4" />Personel Karnesi PDF</Button></div>
          {e.history.map(h => (
            <div key={h.id} className="flex items-center justify-between rounded px-2 py-1 text-sm hover:bg-slate-50">
              <span><b className="font-mono text-xs">{h.request_number || '-'}</b> · {fmtDate(h.created_at)} · {h.is_tipi} / {h.is_cesidi}</span><StarScore value={h.score} />
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    ))}
    {rows.length === 0 && <p className="p-8 text-center text-sm text-slate-500">Henüz puanlanan personel yok</p>}
  </Accordion>
);

export default function FirmFiveStarPage() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/firm/five-star`).then(r => { setD(r.data); window.dispatchEvent(new Event('fivestar:seen')); }).catch(() => setD(false)); }, []);
  if (d === null) return <p className="text-sm text-slate-400">Yükleniyor...</p>;
  if (d === false) return <p className="text-sm text-slate-500">Five Star bilgileri yüklenemedi</p>;
  return (
    <div className="space-y-6" data-testid="firm-five-star-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Star className="h-6 w-6 fill-amber-400 text-amber-400" />Five Star Değerlendirme</h1>
        <p className="text-sm text-slate-500">İSG biriminin sahada yaptığı Five Star denetimlerinde firmanızın ve personelinizin aldığı puanlar</p>
      </div>
      {d.firms.length === 0 ? <div className="rounded-xl border bg-white p-10 text-center text-slate-500" data-testid="firm-five-star-empty">Henüz Five Star denetimi yapılmadı</div> : (
        <>
          <div className="grid gap-3 lg:grid-cols-2">{d.firms.map(f => <FirmStar key={f.id} f={f} />)}</div>
          <Trend trend={d.trend} hideThreshold />
          <Tabs defaultValue="denetim">
            <TabsList>
              <TabsTrigger value="denetim" data-testid="firm-tab-audits"><FileText className="mr-1 h-4 w-4" />Denetimler ({d.audits.length})</TabsTrigger>
              <TabsTrigger value="personel" data-testid="firm-tab-employees"><Users className="mr-1 h-4 w-4" />Personel Puanları ({d.employees.length})</TabsTrigger>
              <TabsTrigger value="eksik" data-testid="firm-tab-repeats">Tekrarlayan Eksiklikler ({d.repeats.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="denetim" className="mt-4"><div className="divide-y rounded-lg border bg-white">{d.audits.map(a => <AuditRow key={a.id} a={a} />)}</div></TabsContent>
            <TabsContent value="personel" className="mt-4"><EmployeeList rows={d.employees} /></TabsContent>
            <TabsContent value="eksik" className="mt-4 rounded-lg border bg-white p-4"><Repeats rows={d.repeats} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
